/**
 * Contexto de Autenticação do Sistema MEDICARE
 * Gerencia o estado de autenticação do usuário
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { Usuario } from '../types';

// Tipos do contexto
interface AuthState {
  usuario: Usuario | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  user: Usuario | null; // Alias para compatibilidade
  login: (email: string, senha: string) => Promise<void>;
  logout: () => void;
  atualizarPerfil: (dados: Partial<Usuario>) => Promise<void>;
  verificarToken: () => Promise<boolean>;
  dispatch: React.Dispatch<AuthAction>; // Adicionar dispatch ao contexto
}

// Ações do reducer
type AuthAction =
  | { type: 'LOGIN_INICIO' }
  | { type: 'LOGIN_SUCESSO'; payload: { usuario: Usuario; token: string } }
  | { type: 'LOGIN_ERRO' }
  | { type: 'LOGOUT' }
  | { type: 'ATUALIZAR_USUARIO'; payload: Usuario }
  | { type: 'SET_LOADING'; payload: boolean };

// Estado inicial
const initialState: AuthState = {
  usuario: null,
  token: localStorage.getItem('medicare_token'),
  isLoading: true,
  isAuthenticated: false,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_INICIO':
      return {
        ...state,
        isLoading: true,
      };
    
    case 'LOGIN_SUCESSO':
      return {
        ...state,
        usuario: action.payload.usuario,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    
    case 'LOGIN_ERRO':
      return {
        ...state,
        usuario: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        usuario: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    
    case 'ATUALIZAR_USUARIO':
      return {
        ...state,
        usuario: action.payload,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    default:
      return state;
  }
};

// Criação do contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider do contexto
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Função de login
  const login = async (email: string, senha: string): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_INICIO' });
      
      const response = await api.post('/auth/login', {
        email,
        senha,
      });
      
      const { usuario, token } = response.data.dados;
      
      // Garantir que o avatar seja extraído corretamente
      const avatarFinal = usuario.avatar || usuario.dados_adicionais?.avatar;
      
      // Garantir compatibilidade entre 'tipo' e 'tipo_usuario'
      const usuarioCompleto = {
        ...usuario,
        tipo: usuario.tipo_usuario, // Garantir que 'tipo' seja definido
        tipo_usuario: usuario.tipo_usuario, // Manter 'tipo_usuario'
        avatar: avatarFinal // Garantir que o avatar seja definido corretamente
      };
      
      // Salvar token no localStorage
      localStorage.setItem('medicare_token', token);
      
      // Configurar token no header da API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      dispatch({
        type: 'LOGIN_SUCESSO',
        payload: { usuario: usuarioCompleto, token },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_ERRO' });
      throw error;
    }
  };

  // Função de logout
  const logout = (): void => {
    // Remover token do localStorage
    localStorage.removeItem('medicare_token');
    
    // Remover token do header da API
    delete api.defaults.headers.common['Authorization'];
    
    dispatch({ type: 'LOGOUT' });
  };

  // Função para atualizar perfil
  const atualizarPerfil = async (dados: Partial<Usuario>): Promise<void> => {
    try {
      const response = await api.put('/usuarios/perfil', dados);
      const usuarioAtualizado = response.data;
      
      dispatch({
        type: 'ATUALIZAR_USUARIO',
        payload: usuarioAtualizado,
      });
    } catch (error) {
      throw error;
    }
  };

  // Função para verificar token
  const verificarToken = async (): Promise<boolean> => {
    try {
      if (!state.token) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }
      
      // Configurar token no header da API
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
      
      // Verificar se o token é válido buscando o perfil completo
      const response = await api.get('/usuarios/perfil');
      const apiData = response.data;
      
      // O backend retorna { sucesso: true, dados: { usuario: {...} } }
      const usuario = apiData.dados?.usuario || apiData;
      
      // Garantir que o avatar seja extraído corretamente
      const avatarFinal = usuario.avatar || usuario.dados_adicionais?.avatar;
      
      // Garantir compatibilidade entre 'tipo' e 'tipo_usuario'
      const usuarioCompleto = {
        ...usuario,
        tipo: usuario.tipo_usuario || usuario.tipo, // Garantir que 'tipo' seja definido
        tipo_usuario: usuario.tipo_usuario || usuario.tipo, // Garantir que 'tipo_usuario' seja definido
        avatar: avatarFinal // Garantir que o avatar seja definido corretamente
      };
      
      dispatch({
        type: 'LOGIN_SUCESSO',
        payload: { usuario: usuarioCompleto, token: state.token },
      });
      
      return true;
    } catch (error) {
      // Token inválido, fazer logout
      logout();
      return false;
    }
  };

  // Verificar token ao inicializar
  useEffect(() => {
    if (state.token && !state.usuario) {
      // Se há token mas não há usuário, verificar token para carregar dados completos
      verificarToken();
    } else if (!state.token) {
      // Se não há token, parar loading
      dispatch({ type: 'SET_LOADING', payload: false });
    } else if (state.usuario) {
      // Usuário já carregado, parar loading
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Valor do contexto
  const contextValue: AuthContextType = {
    ...state,
    user: state.usuario, // Alias para compatibilidade
    login,
    logout,
    atualizarPerfil,
    verificarToken,
    dispatch, // Adicionar dispatch ao valor do contexto
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};

// Hook para verificar permissões
export const usePermissions = () => {
  const { usuario } = useAuth();
  
  return {
    isAdmin: usuario?.tipo === 'admin',
    isMedico: usuario?.tipo === 'medico',
    isEnfermeiro: usuario?.tipo === 'enfermeiro',
    isPaciente: usuario?.tipo === 'paciente',
    isProfissionalSaude: usuario?.tipo === 'medico' || usuario?.tipo === 'enfermeiro',
    canManageUsers: usuario?.tipo === 'admin',
    canManageConsultas: usuario?.tipo === 'admin' || usuario?.tipo === 'medico',
    canViewAllConsultas: usuario?.tipo === 'admin' || usuario?.tipo === 'medico' || usuario?.tipo === 'enfermeiro',
    canManagePrompts: usuario?.tipo === 'admin' || usuario?.tipo === 'medico',
    canManageConfiguracoes: usuario?.tipo === 'admin',
  };
};

export default AuthContext;